"use client";

import React, { useState, useMemo } from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Mail } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BusinessDirectoryConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [selectedCategory, setSelectedCategory] = useState("All Businesses");
  const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});
  const categories = ["All Businesses", "Construction", "Finance", "Real Estate", "Health & Wellness", "Legal", "Logistics", "Entertainment", "Hospitality"];
  const filteredPosts = useMemo(() => {
    if (selectedCategory === "All Businesses") return posts;
    return posts.filter(post => post.customFields?.business_categories === selectedCategory);
  }, [posts, selectedCategory]);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty("--mouse-x", `${x}px`);
    e.currentTarget.style.setProperty("--mouse-y", `${y}px`);
  };
  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {[...Array(6)].map((_, i) => <div key={i} className="h-80 animate-pulse bg-muted/20 rounded-none border-t-2 border-primary/20" />)}
      </div>;
  }
  if (error) return <div className="text-destructive">Error loading directory.</div>;
  return <>
      <div className="mb-12 flex flex-wrap gap-3">
        {categories.map(cat => <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-6 py-2 font-mono text-xs font-bold uppercase transition-all border ${selectedCategory === cat ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground border-muted hover:border-primary hover:text-primary"}`}>
            {cat}
          </button>)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {filteredPosts.map((post, index) => {
        const isSponsor = post.customFields?.sponsor_level === "Major Sponsor" || post.customFields?.sponsor_level === "General Sponsor";
        const businessName = post.customFields?.business_name || post.title;
        const hasLogoError = logoErrors[post.id];
        return <div key={post.id} data-index={index} onMouseMove={handleMouseMove} className="group relative flex h-full flex-col overflow-hidden rounded-none border-t-2 border-primary bg-card/40 backdrop-blur-md transition-colors hover:bg-accent/10">
              {isSponsor && <div className="absolute top-0 left-0 z-20 overflow-hidden size-24 pointer-events-none">
                  <div className="absolute top-4 -left-8 w-32 py-1 bg-destructive text-white text-[10px] font-bold uppercase text-center -rotate-45 shadow-sm">
                    Sponsor
                  </div>
                </div>}

              <div className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
            background: `radial-gradient(400px circle at var(--mouse-x, 0) var(--mouse-y, 0), hsla(45, 85%, 55%, 0.08), transparent 40%)`
          }} />
              
              <Card className="flex h-full flex-col border-none bg-transparent shadow-none">
                <CardHeader className="pb-4">
                  <div className="mb-4 flex items-center justify-start h-16">
                    {post.customFields?.business_logo && !hasLogoError ? <img src={post.customFields.business_logo} alt={businessName} className="h-full w-auto object-contain" loading="lazy" onError={() => setLogoErrors(prev => ({
                  ...prev,
                  [post.id]: true
                }))} /> : <div className="flex h-16 w-16 items-center justify-center rounded-none border border-primary/20 bg-primary/5 font-mono text-xl font-bold text-primary">
                        {getInitials(businessName)}
                      </div>}
                  </div>
                  <CardTitle className="font-default text-xl font-bold uppercase tracking-tight text-foreground">
                    {businessName}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow pb-6">
                  <p className="font-default text-muted-foreground leading-relaxed line-clamp-4 mb-4">
                    {post.customFields?.business_description}
                  </p>
                  {post.customFields?.business_deals && <p className="text-xs italic text-muted-foreground/80 border-l-2 border-primary/30 pl-3">
                      Special Offer: {post.customFields.business_deals}
                    </p>}
                </CardContent>
                <CardFooter className="pt-0 mt-auto flex-wrap gap-4">
                  {post.customFields?.website_url && <Link to={post.customFields.website_url} newTab className="group/link inline-flex items-center gap-2 font-mono text-xs font-bold uppercase text-primary transition-colors hover:text-primary/80">
                      Visit Website
                      <ArrowRight className="size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link>}
                  {post.customFields?.contact_email && <Link to={`mailto:${post.customFields.contact_email}?subject=Inquiry from Birkalla Business Network&cc=admin@birkalla.com`} className="group/link inline-flex items-center gap-2 font-mono text-xs font-bold uppercase text-primary transition-colors hover:text-primary/80">
                      Contact Us
                      <Mail className="size-4 transition-transform duration-300 group-hover/link:scale-110" />
                    </Link>}
                </CardFooter>
              </Card>
            </div>;
      })}
      </div>
      {filteredPosts.length === 0 && <div className="text-center py-12 text-muted-foreground font-mono text-sm">
          No businesses found in this category.
        </div>}
    </>;
}
export default function ExpertSolutionsForEveryPipe() {
  const wp_query_params = {
    post_type: "business_directory",
    per_page: 100,
    orderby: "title",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="223683" id="birkalla-business-directory" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="mx-auto max-w-7xl">
        <div className="mb-16 max-w-2xl">
          <h2 className="font-default text-3xl md:text-5xl font-extrabold tracking-tighter uppercase mb-4 text-foreground">
            Birkalla Business Network
          </h2>
          <p className="font-default text-muted-foreground text-lg">
            Support the local businesses that support our club. A curated directory of professional partners committed to the Birkalla community.
          </p>
          <Link to="/BusinessDirectoryPage" className="mt-6 mb-8 inline-block">
            <button className="px-8 py-3 font-mono text-xs font-bold uppercase transition-all border bg-primary text-primary-foreground border-primary hover:bg-primary/90">
              Browse Directory
            </button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        post_type: "business_directory",
        per_page: 100,
        orderby: "title",
        order: "asc"
      }}>
          <BusinessDirectoryConsumer />
        </PostsProvider>
      </div>
    </section>;
}